import { css } from '@emotion/css';
import { cssVarV2 } from '@streamlain/theme/v2';

export const setupWizardBodyHostStyle = css({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
});

// Translucent backdrop with a subtle frost — reads as modal but keeps
// enough of the gantt visible that the user still has context for
// "why am I here?".
export const wizardBackdropStyle = css({
  position: 'fixed',
  inset: 0,
  background:
    'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 40%, transparent)',
  backdropFilter: 'blur(14px) saturate(180%)',
  WebkitBackdropFilter: 'blur(14px) saturate(180%)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
});

export const wizardContainedBackdropStyle = css({
  position: 'absolute',
  inset: 0,
  boxSizing: 'border-box',
  background:
    'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 40%, transparent)',
  backdropFilter: 'blur(14px) saturate(180%)',
  WebkitBackdropFilter: 'blur(14px) saturate(180%)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '16px',
  overflow: 'auto',
  overscrollBehavior: 'contain',
});

export const wizardContainedDialogStyle = css({
  flex: '0 0 auto',
  maxHeight: '100%',
  maxWidth: '100%',
  overflow: 'auto',
  width: 'min(520px, 100%)',
});

export const wizardInlineContainerStyle = css({
  alignItems: 'stretch',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  justifyContent: 'flex-start',
  overflow: 'visible',
  padding: '20px 24px 24px',
  width: '100%',
});

// Card-style dialog. Rounded 18 px feels contemporary without being
// overly cutesy; padding is generous so sections can breathe.
export const wizardDialogStyle = css({
  background: 'var(--streamlain-v2-layer-background-primary)',
  borderRadius: '16px',
  boxSizing: 'border-box',
  padding: '20px 22px 18px',
  width: '520px',
  maxWidth: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow:
    '0 24px 60px -12px rgba(0,0,0,0.24), 0 0 0 1px var(--streamlain-v2-layer-insideBorder-border)',
  color: 'var(--streamlain-v2-text-primary)',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const wizardHeroStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const wizardTitleStyle = css({
  fontSize: '18px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  margin: 0,
});

export const wizardDescriptionStyle = css({
  fontSize: '13px',
  color: 'var(--streamlain-v2-text-secondary)',
  lineHeight: 1.4,
  margin: 0,
});

// Each step lives in its own rounded card so the wizard reads as a
// short list of decisions rather than a dense form.
export const wizardSectionStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'var(--streamlain-v2-layer-background-secondary)',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
});

export const wizardSectionHeaderStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

export const wizardSectionTitleStyle = css({
  fontSize: '15px',
  fontWeight: 600,
  margin: 0,
});

export const wizardSectionHintStyle = css({
  fontSize: '13px',
  color: 'var(--streamlain-v2-text-secondary)',
  margin: 0,
  lineHeight: 1.5,
});

// Soft hint blurb used under "create new" and unavailable states.
// Same tone as section hints but sits inside the card body so it
// reads as a status line rather than a subtitle.
export const wizardHintStyle = css({
  fontSize: '13px',
  color: 'var(--streamlain-v2-text-secondary)',
  margin: 0,
  lineHeight: 1.5,
  padding: '8px 12px',
  borderRadius: '8px',
  background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
});

export const wizardDisclosureButtonStyle = css({
  alignItems: 'center',
  background: 'var(--streamlain-v2-layer-background-primary)',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  borderRadius: '8px',
  color: 'var(--streamlain-v2-text-secondary)',
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  height: '30px',
  justifyContent: 'center',
  transition:
    'background 160ms ease, border-color 160ms ease, color 160ms ease',
  width: '30px',

  svg: {
    transition: 'transform 180ms ease',
  },

  '&[data-open="true"]': {
    borderColor: 'var(--streamlain-v2-button-primary)',
    color: 'var(--streamlain-v2-button-primary)',

    svg: {
      transform: 'rotate(90deg)',
    },
  },

  ':hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    color: 'var(--streamlain-v2-text-primary)',
  },
});

export const wizardExpandableBodyStyle = css({
  display: 'grid',
  gridTemplateRows: '0fr',
  opacity: 0,
  overflow: 'hidden',
  transition: 'grid-template-rows 200ms ease, opacity 160ms ease',

  '&[data-open="true"]': {
    gridTemplateRows: '1fr',
    opacity: 1,
  },
});

export const wizardExpandableInnerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minHeight: 0,
  overflow: 'hidden',
});

export const wizardAdvancedTriggerStyle = css({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: '8px',
  color: 'var(--streamlain-v2-text-primary)',
  cursor: 'pointer',
  display: 'flex',
  gap: '12px',
  justifyContent: 'space-between',
  padding: '2px 0',
  textAlign: 'left',
  width: '100%',

  ':hover': {
    color: 'var(--streamlain-v2-button-primary)',
  },
});

export const wizardAdvancedHeaderTextStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
});

// iOS-style toggle. Thumb glides right when `data-on=true` is set on
// the track. The checkbox is positioned to fill the track and carries
// the change event; the thumb has `pointer-events: none` so clicks
// never land on the overlay and miss the input underneath.
export const wizardToggleStyle = css({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  width: '44px',
  height: '26px',
  borderRadius: '999px',
  // Off-state uses the grabber token — a mid-neutral grey that reads
  // against the section card (`--streamlain-v2-layer-background-secondary`).
  // `--streamlain-v2-layer-background-hoverOverlay` is too close to the card for inactive
  // toggles to stand out.
  background: cssVarV2('button/grabber/default'),
  cursor: 'pointer',
  transition: 'background 180ms ease',
  flexShrink: 0,

  input: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    cursor: 'pointer',
    margin: 0,
    width: '100%',
    height: '100%',
  },

  '&[data-on=true]': {
    background: 'var(--streamlain-v2-button-primary)',
  },

  '&[data-disabled=true]': {
    opacity: 0.4,
    cursor: 'not-allowed',
    input: { cursor: 'not-allowed' },
  },
});

export const wizardToggleThumbStyle = css({
  position: 'absolute',
  top: '3px',
  left: '3px',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: 'var(--streamlain-v2-layer-background-primary)',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  transition: 'transform 180ms ease',
  // Without this the thumb intercepts clicks that belong to the input
  // underneath, making the toggle feel broken on its left half.
  pointerEvents: 'none',

  '[data-on=true] &': {
    transform: 'translateX(18px)',
  },
});

// Green "we spotted one" chip that sits next to the section title when
// the heuristic name+type match picked an existing column for the user.
// Tiny and muted — the key info is the dropdown below, the badge just
// signals "this section is already wired up, glance and move on".
export const wizardFoundBadgeStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: cssVarV2('status/success'),
  background: `color-mix(in srgb, ${cssVarV2('status/success')} 14%, transparent)`,
  padding: '2px 6px',
  borderRadius: '999px',
  textTransform: 'uppercase',
});

// Grouped radio cards for picking the role of an existing column
// (used by the one-date flow) and the use-existing/create-new
// choice inside the optional sections. Larger targets + lift the
// selected one so the choice is visually obvious.
export const wizardChoiceRowStyle = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '10px',
});

export const wizardChoiceCardStyle = css({
  cursor: 'pointer',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background: 'var(--streamlain-v2-layer-background-primary)',
  fontSize: '13px',
  fontWeight: 500,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  transition: 'border-color 140ms ease, transform 140ms ease',

  input: { display: 'none' },

  '&[data-selected=true]': {
    borderColor: 'var(--streamlain-v2-button-primary)',
    transform: 'translateY(-1px)',
    boxShadow: 'var(--streamlain-v2-shadow-level1)',
  },
});

// Inner row inside the shared "Status tracking" section — 3 of these
// (progress/status/assignee) stack vertically separated by a subtle
// rule so each reads as its own concern without duplicating the
// outer card chrome.
export const wizardTrackingRowStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  paddingTop: '4px',
});

export const wizardFieldStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const wizardLabelStyle = css({
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--streamlain-v2-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
});

// Custom dropdown trigger — replaces `<select>` so the popover options
// render with our tokens (not platform native dark-grey). The chevron
// glyph is inline-SVG so no icon import is required in the template.
export const wizardSelectStyle = css({
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background: 'var(--streamlain-v2-layer-background-primary)',
  color: 'var(--streamlain-v2-text-primary)',
  fontSize: '14px',
  lineHeight: 1.3,
  cursor: 'pointer',
  textAlign: 'left',

  '&:focus-visible': {
    outline: '2px solid var(--streamlain-v2-brand-primary)',
    outlineOffset: '1px',
    borderColor: 'transparent',
  },

  '& svg': {
    width: '14px',
    height: '14px',
    flexShrink: 0,
    color: 'var(--streamlain-v2-text-secondary)',
  },
});

export const wizardSelectPlaceholderStyle = css({
  color: 'var(--streamlain-v2-text-secondary)',
});

export const wizardCreateInputRowStyle = css({
  display: 'flex',
  gap: '6px',
  marginTop: '6px',
});

export const wizardCreateInputStyle = css({
  flex: '1 1 auto',
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid var(--streamlain-v2-button-primary)',
  background: 'var(--streamlain-v2-layer-background-primary)',
  color: 'var(--streamlain-v2-text-primary)',
  fontSize: '14px',
  lineHeight: 1.3,

  '&:focus-visible': {
    outline: '2px solid var(--streamlain-v2-brand-primary)',
    outlineOffset: '1px',
    borderColor: 'transparent',
  },
});

export const wizardCreateCancelStyle = css({
  appearance: 'none',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background: 'var(--streamlain-v2-layer-background-primary)',
  color: 'var(--streamlain-v2-text-secondary)',
  padding: '0 10px',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '16px',
  lineHeight: 1,

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    color: 'var(--streamlain-v2-text-primary)',
  },
});

export const wizardActionsStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  marginTop: '4px',
});

// `&&&` triples the class selector -> (0,3,0). Keeps the CTA styling
// ahead of scoped database viewport theme boundaries. V2 button vars
// stay blue/white across every theme viewport.
export const wizardPrimaryButtonStyle = css({
  appearance: 'none',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '999px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'filter 120ms ease, transform 120ms ease',

  '&&&': {
    background: 'var(--streamlain-v2-button-primary)',
    color: 'var(--streamlain-v2-button-pureWhiteText)',
  },

  '&:hover:not(:disabled)': {
    filter: 'brightness(1.05)',
  },

  '&:active:not(:disabled)': {
    transform: 'scale(0.98)',
  },

  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
});

export const wizardSecondaryButtonStyle = css({
  appearance: 'none',
  padding: '10px 20px',
  borderRadius: '999px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background 120ms ease',

  '&&&': {
    background: 'transparent',
    color: 'var(--streamlain-v2-text-primary)',
    border: 'none',
  },

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
  },
});

// Kept for the single-date role picker (fallback path).
export const wizardRadioRowStyle = css({
  display: 'flex',
  gap: '12px',
  fontSize: '13px',
});
